/* ALMA: independent instances; no original Desert selectors/controllers are replaced. */
const BaseElement = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;
const config = element => { try { return JSON.parse(element?.getAttribute('data-alma-config') || '{}'); } catch { return {}; } };
const json = (root, selector) => { const node=root.querySelector(selector); return node ? JSON.parse(node.textContent) : null; };
const visible = element => !!element && !element.hidden && element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden';
const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
export function productRequestURL(productUrl, currentHref, sectionId, optionIds, view = 'alma-pdp') {
  const url=new URL(currentHref), target=new URL(productUrl, url);
  if (url.origin!==target.origin) throw new Error('Destino de producto incompatible');
  url.pathname=target.pathname;
  url.searchParams.delete('variant'); url.searchParams.delete('sections'); url.searchParams.delete('section_id'); url.searchParams.delete('option_values');
  if (view && !url.searchParams.has('view')) url.searchParams.set('view',view);
  if (sectionId) url.searchParams.set('section_id',sectionId);
  if (optionIds?.filter(Boolean).length) url.searchParams.set('option_values',optionIds.filter(Boolean).join(','));
  return url;
}
export function clampQuantity(value, minimum=1, increment=1, maximum=null) {
  const min=Math.max(1,Number(minimum)||1), step=Math.max(1,Number(increment)||1);
  let q=Math.max(min,Number.isFinite(Number(value))?Number(value):min);
  q=min+Math.floor((q-min)/step)*step;
  if (maximum!==null && maximum!=='' && Number(maximum)>=min) q=Math.min(q,min+Math.floor((Number(maximum)-min)/step)*step);
  return q;
}
export function whatsappURL(base, template, {product='',url='',variant=''}={}) {
  const link=new URL(base);
  if(link.protocol!=='https:') throw new Error('URL inválida');
  const substitutions={producto:product,url,variante:variant};
  link.searchParams.set('text',String(template||'').replace(/\{(producto|url|variante)\}/g,(_,key)=>substitutions[key]||''));
  return link.href;
}
function priceMarkup(money,settings={}) {
  if (!money) return '';
  let value=settings.trim_zero_decimals ? money.trim : money.store;
  if(settings.currency_mode==='with_code') value=String(value||'').split(money.code).join('').trim();
  return value || '';
}
function setMoney(root,data,complete) {
  const block=root.closest('[data-alma-block]'), s=config(block);
  const variant=complete?data.variant:null, money=variant?data.money:(data.minMoney||data.money);
  root.querySelector('[data-alma-amount]').innerHTML=priceMarkup(money,s);
  const currency=root.querySelector('[data-alma-currency]'); currency.textContent=' '+(money?.code||''); currency.hidden=s.currency_mode!=='with_code';
  root.querySelector('[data-alma-from]').hidden=!!variant||!data.priceVaries;
  const compare=root.querySelector('[data-alma-compare]');
  compare.hidden=!(s.show_compare_price && variant && variant.compare>variant.price);
  compare.innerHTML=compare.hidden?'':priceMarkup(data.compareMoney,s);
  const unit=block.querySelector('[data-alma-unit]');
  if(unit){unit.hidden=!(variant&&data.unitReference);unit.innerHTML=unit.hidden?'':data.unitMoney+' / '+data.unitReference;}
}
class AlmaBase extends BaseElement {
  connectedCallback(){
    if(this.life)return;
    this.life=new AbortController();this.settings=config(this);
    const start=()=>{if(this.isConnected && !this.initialized){this.initialized=true;this.init();}};
    if(document.readyState==='loading')this.on(document,'DOMContentLoaded',start,{once:true});else queueMicrotask(start);
  }
  on(target,type,handler,options={}) { target?.addEventListener(type,handler,{...options,signal:this.life.signal}); }
  init(){
    const hideFailedImage=image=>{if(image.matches('[data-alma-custom-image]')){image.hidden=true;const container=image.closest('[data-alma-image-container]');if(container)container.hidden=true;}};
    this.on(this,'error',event=>{if(event.target instanceof HTMLImageElement)hideFailedImage(event.target);},{capture:true});
    this.querySelectorAll('[data-alma-custom-image]').forEach(image=>{if(image.complete&&!image.naturalWidth)hideFailedImage(image);});
    this.on(this,'click',event=>{
      if(event.target.closest('alma-pdp-product,alma-pdp-section')!==this)return;
      const open=event.target.closest('[data-alma-open-dialog]');
      if(open){const dialog=this.querySelector('#'+CSS.escape(open.dataset.almaOpenDialog));if(dialog){this.dialogTrigger=open;open.setAttribute('aria-expanded','true');dialog.showModal();}return;}
      const close=event.target.closest('[data-alma-close-dialog]');if(close) close.closest('dialog')?.close();
    });
    this.querySelectorAll('dialog').forEach(dialog=>{
      this.on(dialog,'close',()=>{this.dialogTrigger?.setAttribute('aria-expanded','false');this.dialogTrigger?.focus();});
      this.on(dialog,'click',e=>{if(e.target===dialog){const r=dialog.getBoundingClientRect();if(e.clientX<r.left||e.clientX>r.right||e.clientY<r.top||e.clientY>r.bottom)dialog.close();}});
    });
    this.on(this,'shopify:block:select',event=>{
      if(event.target.closest('alma-pdp-product,alma-pdp-section')!==this)return;
      const block=event.target.closest('[data-alma-block]')||event.target;
      const details=block.querySelector('details');if(details)details.open=true;
      block.scrollIntoView({block:'nearest',behavior:reduced()?'instant':'smooth'});
    });
  }
  disconnectedCallback(){this.life?.abort();this.life=null;this.initialized=false;this.request?.abort();this.observer?.disconnect();}
}
class AlmaProduct extends AlmaBase {
  init(){
    super.init();
    this.data=json(this,'[data-alma-product-data]');if(!this.data)return;
    this.initialData=structuredClone(this.data);this.sequence=0;this.resolving=false;this.submitting=false;this.failed=false;
    this.form=this.querySelector('[data-alma-form]');this.buy=this.form?.closest('[data-alma-block]');this.labels=config(this.buy);
    this.selected=this.data.options.map(o=>o.values.find(v=>v.selected)?.id||'');
    this.initialSelection();this.sync();
    this.on(this,'change',event=>{
      const input=event.target.closest('[data-alma-option-input],[data-alma-option-select]');
      if(input){this.select(input);return;}
      if(event.target.matches('[data-alma-quantity]')){event.target.value=this.quantity(event.target.value);}
    });
    this.on(this,'click',event=>{
      const button=event.target.closest('[data-alma-quantity-step]');
      if(button){const input=button.parentElement.querySelector('[data-alma-quantity]');input.value=this.quantity(Number(input.value)+Number(button.dataset.almaQuantityStep)*(this.data.variant?.increment||1));input.dispatchEvent(new Event('change',{bubbles:true}));}
      if(event.target.closest('[data-alma-retry-variant]'))this.resolve();
    });
    this.on(this.form,'submit',event=>this.submit(event));
    this.on(window,'resize',()=>{this.reconcile();this.sticky();});
    this.on(window,'popstate',()=>this.restoreHistory());
    this.observer=new ResizeObserver(()=>this.sticky());this.observer.observe(this);
    this.reconcile();this.sticky();
  }
  initialSelection(){
    const p=new URL(location.href).searchParams, id=p.get('variant');
    const explicit=!!id&&id===this.data.explicitVariantId;
    const optionIds=p.get('option_values')?.split(',');
    const validOptions=optionIds?.length===this.data.options.length && this.data.options.every((o,i)=>o.values.some(v=>v.id===optionIds[i]));
    if(validOptions)this.selected=optionIds;
    else if(id&&!explicit){this.selected=this.selected.map(()=> '');this.data.variant=null;}
    else if(!explicit&&this.data.hasSize&&this.data.requireSize){
      this.querySelectorAll('[data-alma-option-position][data-is-size="true"]').forEach(o=>{this.selected[Number(o.dataset.almaOptionPosition)-1]='';});
      this.data.variant=null;
    }
  }
  complete(){return this.data.onlyDefault || this.selected.every(Boolean);}
  quantity(value){const v=this.data.variant;return clampQuantity(value,v?.minimum,v?.increment,v?.maximum);}
  reconcile(){
    const blocks=[...this.querySelector('.alma-pdp-purchase-zone')?.children||[]].filter(el=>el.matches('[data-alma-block]')&&visible(el));
    this.querySelectorAll('[data-alma-block="finance"]').forEach(el=>{el.dataset.priceAdjacent=String(blocks[blocks.indexOf(el)-1]?.dataset.almaBlock==='price');});
    if(!this.form)return;
    const primary=this.querySelector('[data-alma-block="variants"]');
    const fallback=this.form.querySelector('[data-alma-fallback]');
    if(fallback)fallback.hidden=visible(primary)||this.data.onlyDefault;
    const qty=this.querySelector('[data-alma-quantity]'), fallbackQty=this.form.querySelector('[data-alma-fallback-quantity]');
    const useQuantity=visible(qty?.closest('[data-alma-block="quantity"]'));
    if(qty)qty.disabled=!useQuantity;
    if(fallbackQty){fallbackQty.disabled=useQuantity;fallbackQty.value=this.data.variant?.minimum||1;}
  }
  sync(){
    this.querySelectorAll('[data-alma-option-position]').forEach(field=>{
      field.disabled=this.submitting;
      const pos=Number(field.dataset.almaOptionPosition)-1, selected=this.selected[pos], option=this.data.options[pos];
      if(!option)return;
      field.querySelectorAll('[data-alma-option-input]').forEach(input=>{input.checked=input.value===selected;const value=option.values.find(v=>v.id===input.value);input.closest('label').dataset.available=String(value?.available!==false);});
      const select=field.querySelector('select');if(select){select.value=selected||'';[...select.options].forEach(opt=>{const value=option.values.find(v=>v.id===opt.value);if(value)opt.dataset.available=String(value.available);});}
      const selectedName=field.querySelector('[data-alma-selected-name]');if(selectedName)selectedName.textContent=option.values.find(v=>v.id===selected)?.name||'';
    });
    const complete=this.complete(), variant=complete?this.data.variant:null;
    this.querySelectorAll('[data-alma-price]').forEach(el=>setMoney(el,this.data,complete));
    this.querySelectorAll('[data-alma-selection-message]').forEach(el=>{
      const s=config(el.closest('[data-alma-options]'));
      el.hidden=this.data.onlyDefault || (complete && !!variant);
      if(!complete)el.textContent=s.choose_label||this.labels.choose_label||'Selecciona una opción';
      else if(!variant)el.textContent=s.selection_error||'Selecciona una opción disponible';
      else el.textContent='';
    });
    if(this.form){
      const valid=!!variant?.available && !this.resolving && !this.failed && !this.submitting;
      const id=this.form.querySelector('[data-alma-variant-id]');id.value=variant?.id||'';id.disabled=!valid;
      const submit=this.form.querySelector('[data-alma-submit]');submit.disabled=!valid;submit.setAttribute('aria-disabled',String(!valid));
      let label=this.labels.available_label||'Agregar al carrito';
      if(this.submitting||this.resolving)label=this.labels.loading_label||'Agregando…';
      else if(!complete)label=this.labels.choose_label||'Selecciona tu talla';
      else if(!variant||this.failed)label=this.labels.unavailable_label||'No disponible';
      else if(!variant.available)label=this.labels.sold_out_label||'Agotado';
      else if(variant.inventoryManagement && variant.inventoryPolicy==='continue' && variant.inventoryQuantity<=0 && this.labels.preorder_label)label=this.labels.preorder_label;
      this.form.querySelector('[data-alma-submit-label]').textContent=label;
      const accelerated=this.form.querySelector('[data-alma-accelerated]');if(accelerated)accelerated.hidden=!valid;
      this.form.querySelectorAll('shopify-payment-terms').forEach(el=>{if(variant)el.setAttribute('variant-id',variant.id);});
    }
    this.querySelectorAll('[data-alma-quantity]').forEach(input=>{input.min=variant?.minimum||1;input.step=variant?.increment||1;if(variant?.maximum!=null)input.max=variant.maximum;else input.removeAttribute('max');input.value=this.quantity(input.value);});
    if(variant?.mediaId)this.querySelector('alma-pdp-gallery')?.selectMedia(variant.mediaId);
    this.whatsapp();this.reconcile();
  }
  whatsapp(){
    const publicURL=new URL(this.data.productUrl,location.origin);if(this.complete()&&this.data.variant)publicURL.searchParams.set('variant',this.data.variant.id);
    this.querySelectorAll('[data-alma-whatsapp]').forEach(link=>{try{link.href=whatsappURL(link.dataset.waUrl,link.dataset.waMessage,{product:this.data.title,url:publicURL.href,variant:this.complete()&&this.data.variant?.title!=='Default Title'?this.data.variant?.title:''});}catch{link.removeAttribute('href');link.hidden=true;}});
  }
  select(input){
    if(this.submitting)return;
    const index=Number(input.dataset.position)-1;
    const option=input.matches('select')?input.selectedOptions[0]:input;
    const nextUrl=option?.dataset.productUrl;
    if(nextUrl && new URL(nextUrl,location.origin).pathname!==new URL(this.data.productUrl,location.origin).pathname){
      const url=productRequestURL(nextUrl,location.href,null,[],this.dataset.almaView||'alma-pdp');location.assign(url.href);return;
    }
    this.selected[index]=input.value;
    this.failed=false;this.message('');this.resolve();
  }
  async resolve(directURL=null){
    const seq=++this.sequence;this.request?.abort();this.request=new AbortController();this.resolving=true;this.failed=false;this.sync();
    const wanted=[...this.selected];
    const url=directURL||productRequestURL(this.data.productUrl,location.href,this.dataset.sectionId,wanted,this.dataset.almaView||'alma-pdp');
    try{
      const response=await fetch(url,{signal:this.request.signal,headers:{'X-Requested-With':'XMLHttpRequest'}});
      if(!response.ok)throw new Error('No se pudo actualizar el producto');
      const doc=new DOMParser().parseFromString(await response.text(),'text/html');
      const root=doc.querySelector('alma-pdp-product');
      const incoming=root&&json(root,'[data-alma-product-data]');
      if(!incoming || incoming.productId!==this.data.productId || incoming.sectionId!==this.dataset.sectionId)throw new Error('Respuesta de otra sección o producto');
      if(seq!==this.sequence||!this.isConnected)return;
      if(directURL){this.selected=incoming.options.map(o=>o.values.find(v=>v.selected)?.id||'');this.data={...incoming,requireSize:this.data.requireSize,hasSize:this.data.hasSize};this.initialSelection();}
      else{
        const received=incoming.options.map(o=>o.values.find(v=>v.selected)?.id||'');
        if(wanted.every(Boolean) && wanted.some((v,i)=>v!==received[i]))throw new Error('La respuesta no conserva las opciones elegidas');
        // Only commercial data changes: existing DOM settings, styles and labels remain intact.
        this.data={...incoming,requireSize:this.data.requireSize,hasSize:this.data.hasSize};
        this.selected=wanted;
      }
      this.resolving=false;this.sync();
      if(!directURL&&!window.Shopify?.designMode){
        const display=productRequestURL(this.data.productUrl,location.href,null,this.selected,this.dataset.almaView||'alma-pdp');
        if(this.complete()&&this.data.variant)display.searchParams.set('variant',this.data.variant.id);
        history.replaceState({},'',display);
      }
    }catch(error){
      if(error.name==='AbortError'||seq!==this.sequence)return;
      this.failed=true;this.data.variant=null;this.message(this.labels.error_label||'No se pudo actualizar. Inténtalo de nuevo.',true);
    }finally{if(seq===this.sequence&&this.isConnected){this.resolving=false;this.sync();}}
  }
  restoreHistory(){const url=new URL(location.href);url.searchParams.set('section_id',this.dataset.sectionId);if(!url.searchParams.has('view'))url.searchParams.set('view',this.dataset.almaView||'alma-pdp');this.resolve(url);}
  message(text,retry=false){
    const node=this.form?.querySelector('[data-alma-form-message]');if(!node)return;
    node.textContent=text;node.hidden=!text;
    let button=this.form.querySelector('[data-alma-retry-variant]');
    if(retry&&!button){button=document.createElement('button');button.type='button';button.dataset.almaRetryVariant='';button.className='alma-pdp-text-link';button.textContent='Reintentar selección';node.after(button);}
    if(button)button.hidden=!retry;
  }
  async submit(event){
    event.preventDefault();
    if(this.submitting||this.resolving||this.failed||!this.complete()||!this.data.variant?.available)return;
    this.reconcile();
    const quantity=[...this.form.elements].find(el=>el.name==='quantity'&&!el.disabled);
    if(quantity&&!quantity.checkValidity()){quantity.reportValidity();return;}
    const body=new FormData(this.form);body.set('id',this.data.variant.id);body.set('quantity',this.quantity(quantity?.value));
    for(const key of [...body.keys()])if(key.startsWith('alma-option-'))body.delete(key);
    const drawer=document.querySelector('cart-drawer');
    const sections=typeof drawer?.getSectionsToRender==='function'?drawer.getSectionsToRender():[];
    if(sections.length){body.set('sections',sections.map(s=>s.section||s.id).join(','));const context=new URL(location.href);body.set('sections_url',context.pathname+context.search);}
    this.submitting=true;this.message('');this.sync();let added=false;
    try{
      const response=await fetch(this.data.addUrl,{method:'POST',headers:{Accept:'application/json','X-Requested-With':'XMLHttpRequest'},body});
      const result=await response.json();
      if(!response.ok||result.status){this.message(String(result.description||result.message||this.labels.error_label));return;}
      added=true;if(!this.isConnected)return;
      this.message(this.labels.success_label||'Producto agregado');
      const recovery=this.form.querySelector('[data-alma-cart-recovery]');if(recovery)recovery.hidden=false;
      // A presentation error below never resubmits the successful POST.
      if(drawer&&typeof drawer.renderContents==='function'&&sections.every(s=>result.sections?.[s.id||s.section])){
        try{drawer.renderContents(result);drawer.openDrawer?.({preventDefault(){},target:event.submitter||this.form.querySelector('[data-alma-submit]')});}catch{ /* cart recovery link remains available */ }
      }
    }catch{
      this.message(added?(this.labels.success_label||'Producto agregado'):(this.labels.error_label||'No se pudo agregar. Comprueba el carrito antes de reintentar.'));
      const recovery=this.form?.querySelector('[data-alma-cart-recovery]');if(recovery)recovery.hidden=false;
    }finally{this.submitting=false;if(this.isConnected)this.sync();}
  }
  sticky(){
    const mode=this.settings.sticky_mode;if(!mode||mode==='none'){delete this.dataset.stickyReady;return;}
    const column=this.querySelector(mode==='gallery'?'.alma-pdp-media-zone':'.alma-pdp-purchase-zone');
    // Read the existing global header only; never restyle or replace it.
    const header=document.querySelector('header');const headerHeight=Math.max(0,header?.getBoundingClientRect().bottom||0);
    const top=headerHeight+(Number(this.settings.sticky_offset)||0);
    this.style.setProperty('--alma-sticky-top',top+'px');
    if(innerWidth>=Number(this.settings.columns_breakpoint||1024)&&column&&column.getBoundingClientRect().height<innerHeight-top-16)this.dataset.stickyReady=mode;else delete this.dataset.stickyReady;
  }
}
class AlmaGallery extends AlmaBase {
  init(){
    this.visor=this.querySelector('[data-alma-visor]');this.thumbs=this.querySelector('[data-alma-thumbs]');
    this.slides=[...this.visor.querySelectorAll('.alma-pdp-slide')];this.active=0;
    const start=()=>{if(!this.isConnected||this.started)return;if(!window.Swiper&&document.readyState!=='complete'){this.on(window,'load',start,{once:true});return;}this.started=true;this.startSliders();};
    if(document.readyState==='loading')this.on(document,'DOMContentLoaded',start,{once:true});else start();
    this.on(this,'click',event=>{
      const thumb=event.target.closest('[data-alma-thumb]');if(thumb)this.selectMedia(thumb.dataset.almaThumb);
      if(event.target.closest('[data-alma-prev]'))this.go(this.active-1);
      if(event.target.closest('[data-alma-next]'))this.go(this.active+1);
    });
    this.on(this,'focusin',event=>{const thumb=event.target.closest('[data-alma-thumb]');if(thumb)this.selectMedia(thumb.dataset.almaThumb);});
    this.on(window,'resize',()=>this.resize());
    this.observer=new ResizeObserver(()=>{this.style.setProperty('--alma-visor-height',this.visor.offsetHeight+'px');this.thumbSwiper?.update();});this.observer.observe(this.visor);
    if(this.settings.enable_zoom)this.startZoom();
  }
  startSliders(){
    if(window.Swiper && this.slides.length>1){
      this.swiper=new window.Swiper(this.visor,{slidesPerView:1,spaceBetween:0,loop:false,autoHeight:true,speed:reduced()?0:280,a11y:{enabled:true},on:{slideChange:()=>this.changed(this.swiper.activeIndex)}});
      if(this.thumbs)this.thumbSwiper=new window.Swiper(this.thumbs,{slidesPerView:Math.min(this.slides.length,Number(this.settings.thumbs_visible_mobile)||3),spaceBetween:Number(this.settings.thumb_gap_mobile)||0,loop:false,watchSlidesProgress:true,freeMode:false});
    }
    this.resize();this.selectMedia(this.dataset.initialMedia);this.changed(this.swiper?.activeIndex||0);
  }
  resize(){
    const desktop=innerWidth>=768;
    const count=Math.min(this.slides.length,Number(desktop?this.settings.thumbs_visible:this.settings.thumbs_visible_mobile)||3);
    const gap=Number(desktop?this.settings.thumb_gap:this.settings.thumb_gap_mobile)||0;
    const position=desktop?this.settings.thumb_position:this.settings.thumb_position_mobile;
    this.style.setProperty('--alma-thumbs-effective',count);
    if(this.thumbs)this.thumbs.hidden=this.slides.length<2||position==='hidden';
    this.querySelector('.alma-pdp-gallery-arrows')?.toggleAttribute('hidden',this.slides.length<2);
    if(this.thumbSwiper){this.thumbSwiper.params.slidesPerView=count;this.thumbSwiper.params.spaceBetween=gap;this.thumbSwiper.changeDirection(desktop&&['left','right'].includes(position)?'vertical':'horizontal',false);this.thumbSwiper.update();}
    this.swiper?.update();
  }
  changed(index){this.active=index;const id=this.slides[index]?.dataset.almaMediaId;this.querySelectorAll('[data-alma-thumb]').forEach(el=>{if(el.dataset.almaThumb===id)el.setAttribute('aria-current','true');else el.removeAttribute('aria-current');});this.thumbSwiper?.slideTo(index);this.slides.forEach((slide,i)=>{slide.inert=i!==index;slide.setAttribute('aria-hidden',String(i!==index));if(i!==index){slide.querySelectorAll('video').forEach(v=>v.pause());slide.querySelectorAll('iframe').forEach(frame=>{try{const origin=new URL(frame.src).origin;if(/youtube(?:-nocookie)?\.com$/.test(new URL(origin).hostname))frame.contentWindow?.postMessage(JSON.stringify({event:'command',func:'pauseVideo',args:[]}),origin);else if(new URL(origin).hostname==='player.vimeo.com')frame.contentWindow?.postMessage(JSON.stringify({method:'pause'}),origin);}catch{}});slide.querySelectorAll('model-viewer').forEach(model=>model.pause?.());}});}
  go(index){index=Math.max(0,Math.min(index,this.slides.length-1));if(this.swiper)this.swiper.slideTo(index);else{this.visor.firstElementChild.scrollTo({left:index*this.visor.clientWidth,behavior:reduced()?'instant':'smooth'});this.changed(index);}}
  selectMedia(id){const index=this.slides?.findIndex(el=>el.dataset.almaMediaId===String(id));if(index>=0)this.go(index);}
  async startZoom(){
    try{const [{default:Lightbox},{default:PhotoSwipe}]=await Promise.all([import(this.dataset.pswpLightbox),import(this.dataset.pswpModule)]);if(!this.isConnected)return;
      this.lightbox=new Lightbox({gallery:this,children:'a[data-alma-zoom]',pswpModule:PhotoSwipe,appendToEl:this,mainClass:'alma-pdp-zoom',showHideAnimationType:reduced()?'none':'fade',returnFocus:true});this.lightbox.init();
    }catch{ /* zoom anchors still open the original image without a new dependency */ }
  }
  disconnectedCallback(){super.disconnectedCallback();this.swiper?.destroy(true,true);this.thumbSwiper?.destroy(true,true);this.lightbox?.destroy();this.swiper=null;this.thumbSwiper=null;this.lightbox=null;this.started=false;}
}
class AlmaSection extends AlmaBase {
  init(){
    super.init();this.kind=this.dataset.almaKind;
    const opened=[...this.querySelectorAll('details[open]')];if(!this.settings.allow_multiple)opened.slice(1).forEach(el=>el.open=false);
    this.querySelectorAll('details').forEach(details=>{
      const sync=()=>details.querySelector('summary')?.setAttribute('aria-expanded',String(details.open));sync();
      this.on(details,'toggle',()=>{sync();if(details.open&&!this.settings.allow_multiple)this.querySelectorAll('details[open]').forEach(other=>{if(other!==details)other.open=false;});});
    });
    if(this.kind==='details'&&!this.querySelector('details')&&!window.Shopify?.designMode)this.hidden=true;
    this.cards=this.querySelector('[data-alma-cards]');
    if(this.cards){
      this.cardSettings=config(this.cards);this.products=json(this,'[data-alma-products]')||[];this.renderCards();
      this.on(this,'click',e=>{const next=e.target.closest('[data-alma-cards-next]'),prev=e.target.closest('[data-alma-cards-prev]');if(next||prev){const track=this.cards.querySelector('[data-alma-card-track]');track.scrollBy({left:track.clientWidth*(next?1:-1),behavior:reduced()?'instant':'smooth'});}});
      this.on(window,'resize',()=>{if(this.kind==='related'&&this.settings.deduplicate_look)this.renderCards();else this.cardNavigation();});
      this.observer=new ResizeObserver(()=>this.cardNavigation());this.observer.observe(this.cards);
      if(this.kind==='related'){this.on(document,'alma:look-ready',()=>this.renderCards());if(this.settings.source_mode==='automatic'&&!this.products.length)this.recommend();}
      if(this.kind==='look')document.dispatchEvent(new CustomEvent('alma:look-ready',{detail:{productId:this.dataset.productId}}));
    }
    this.querySelectorAll('.alma-pdp-about-media,.alma-pdp-about-copy').forEach(el=>{if(!el.textContent.trim()&&!el.querySelector('img,video,model-viewer'))el.replaceChildren();});
  }
  disconnectedCallback(){super.disconnectedCallback();if(this.kind==='look')queueMicrotask(()=>document.dispatchEvent(new CustomEvent('alma:look-ready',{detail:{productId:this.dataset.productId}})));}
  renderCards(){
    const track=this.cards.querySelector('[data-alma-card-track]'),template=this.cards.querySelector('[data-alma-card-template]');if(!template)return;
    let items=this.products.filter(p=>String(p.id)!==this.dataset.productId&&(this.settings.sold_out_policy!=='hide'||p.available));
    if(this.kind==='related'&&this.settings.deduplicate_look){const selected=new Set([...document.querySelectorAll('alma-pdp-section[data-alma-kind="look"]')].filter(el=>el.dataset.productId===this.dataset.productId&&visible(el)).flatMap(el=>[...el.querySelectorAll('[data-alma-card]')].map(card=>card.dataset.productId)));items=items.filter(p=>!selected.has(String(p.id)));}
    const seen=new Set();items=items.filter(p=>{if(seen.has(p.id))return false;seen.add(p.id);return true;}).slice(0,Number(this.settings.limit)||4);
    track.replaceChildren();
    items.forEach(p=>{
      const card=template.content.firstElementChild.cloneNode(true);card.dataset.productId=p.id;card.dataset.available=p.available;
      card.querySelectorAll('[data-card-link]').forEach(link=>{const url=new URL(p.url,location.origin);const current=new URL(location.href);for(const key of ['view','preview_theme_id','_fd','pb'])if(current.searchParams.has(key))url.searchParams.set(key,current.searchParams.get(key));link.href=url.href;});
      card.querySelector('[data-card-title]').textContent=p.title;
      const image=card.querySelector('img');if(p.image){image.src=p.image;image.alt=p.imageAlt||p.title;image.width=p.width||640;image.height=p.height||800;}else card.querySelector('[data-card-image-link]').hidden=true;
      const price=card.querySelector('[data-card-price]');if(price){price.innerHTML=priceMarkup(p.money,this.cardSettings);if(this.cardSettings.currency_mode==='with_code')price.append(document.createTextNode(' '+p.money.code));card.querySelector('[data-card-from]').hidden=!p.priceVaries;}
      track.append(card);
    });
    if(!window.Shopify?.designMode)this.hidden=!items.length;
    this.cardNavigation();
  }
  cardNavigation(){if(!this.cards)return;const s=this.cardSettings,carousel=(innerWidth>=768?s.layout:s.layout_mobile)==='carousel';const track=this.cards.querySelector('[data-alma-card-track]');this.cards.querySelector('[data-alma-card-nav]').hidden=!carousel||track.scrollWidth<=track.clientWidth+2;}
  async recommend(){
    this.request?.abort();this.request=new AbortController();
    try{
      const url=new URL(this.dataset.recommendationsUrl,location.origin);const current=new URL(location.href);
      for(const key of ['view','preview_theme_id','_fd','pb'])if(current.searchParams.has(key))url.searchParams.set(key,current.searchParams.get(key));
      url.searchParams.set('product_id',this.dataset.productId);url.searchParams.set('section_id',this.dataset.sectionId);url.searchParams.set('limit',Math.min(10,Number(this.settings.limit)||4));url.searchParams.set('intent','related');
      const response=await fetch(url,{signal:this.request.signal});if(!response.ok)throw new Error('Sin recomendaciones');
      const doc=new DOMParser().parseFromString(await response.text(),'text/html');const root=doc.querySelector('alma-pdp-section[data-alma-kind="related"]');
      if(!root||root.dataset.sectionId!==this.dataset.sectionId)throw new Error('Respuesta incompatible');
      const products=json(root,'[data-alma-products]');if(products?.length)this.products=products;
      if(this.isConnected)this.renderCards();
    }catch(e){if(e.name!=='AbortError'&&this.isConnected)this.renderCards();}
  }
}
// Details keeps its Shopify wrapper, block identities and the original DOM nodes.
// A comment records its template position; no second copy or second set of IDs.
class AlmaDetailsPlacement {
  constructor(){
    this.records=new Map();this.unloading=new WeakSet();this.life=new AbortController();
    const on=(target,name,handler,options={})=>target.addEventListener(name,handler,{...options,signal:this.life.signal});
    on(window,'resize',()=>this.schedule());
    on(document,'DOMContentLoaded',()=>this.schedule());
    on(document,'shopify:section:unload',event=>this.unload(event),{capture:true});
    on(document,'shopify:section:load',event=>{
      event.target.querySelectorAll('alma-pdp-product').forEach(main=>this.unloading.delete(main));
      for(const record of this.records.values())if(event.target===record.host||event.target.contains(record.host))record.suspended=false;
      this.schedule();
    });
    on(document,'shopify:section:reorder',event=>{
      for(const record of this.records.values()){
        if(event.target===record.host && record.host.parentElement!==record.main?.querySelector('.alma-pdp-purchase-zone')){
          record.host.before(record.anchor);record.mounted=false;record.host.classList.remove('alma-pdp-details-mounted');
        }
      }
      this.schedule();
    });
    for(const name of ['shopify:section:select','shopify:block:select'])on(document,name,event=>{
      this.layout();
      const selected=event.target.closest('[data-alma-block]')||event.target;
      if(selected.closest('.alma-pdp-details-mounted'))selected.scrollIntoView({block:'nearest',behavior:'instant'});
    });
    this.observer=new MutationObserver(mutations=>{
      if(mutations.some(m=>[...m.addedNodes,...m.removedNodes].some(n=>n.nodeType===1&&(n.matches('.alma-pdp-host,alma-pdp-product,alma-pdp-section')||n.querySelector('alma-pdp-product,alma-pdp-section')))))this.schedule();
    });
    this.observer.observe(document.documentElement,{childList:true,subtree:true});
    this.schedule();
  }
  schedule(){if(this.frame)return;this.frame=requestAnimationFrame(()=>{this.frame=0;this.layout();});}
  move(host,parent,before=null){
    const focus=host.contains(document.activeElement)?document.activeElement:null;
    parent.insertBefore(host,before);
    if(focus?.isConnected)focus.focus({preventScroll:true});
  }
  restore(record){
    if(record.mounted && record.anchor.isConnected)this.move(record.host,record.anchor.parentNode,record.anchor.nextSibling);
    record.host.classList.remove('alma-pdp-details-mounted','alma-pdp-details-empty');record.mounted=false;
  }
  unload(event){
    const target=event.target;
    target.querySelectorAll('alma-pdp-product').forEach(main=>this.unloading.add(main));
    // Restore children before Shopify replaces/deletes a main wrapper.
    for(const record of this.records.values()){
      if(target===record.host){this.restore(record);record.suspended=true;}
      else if(record.main && (target===record.main||target.contains(record.main)))this.restore(record);
    }
  }
  layout(){
    if(this.placing)return;this.placing=true;
    try{
      for(const [host,record] of this.records){
        if(!host.isConnected){
          if(record.mounted && record.main && !record.main.isConnected && record.anchor.isConnected)this.restore(record);
          else{record.anchor.remove();this.records.delete(host);continue;}
        }
        const root=host.matches('alma-pdp-section')?host:host.querySelector('alma-pdp-section[data-alma-kind="details"]');
        if(!root){this.restore(record);record.anchor.remove();this.records.delete(host);continue;}
        if(root!==record.root){record.root=root;record.suspended=false;}
      }
      document.querySelectorAll('alma-pdp-section[data-alma-kind="details"]').forEach(root=>{
        const host=root.closest('.shopify-section')||root.closest('.alma-pdp-host')||root;
        if(this.records.has(host))return;
        const anchor=document.createComment(' ALMA: posición original de Detalles ');host.before(anchor);
        this.records.set(host,{root,host,anchor,mounted:false,main:null,suspended:false});
      });
      const mains=[...document.querySelectorAll('alma-pdp-product')];
      const placements=new Map();
      const ordered=[...this.records.values()].sort((a,b)=>a.anchor.compareDocumentPosition(b.anchor)&Node.DOCUMENT_POSITION_FOLLOWING?-1:1);
      for(const record of ordered){
        if(record.suspended)continue;
        const main=mains.find(candidate=>!this.unloading.has(candidate)&&candidate.dataset.productId===record.root.dataset.productId&&candidate.querySelector('.alma-pdp-hero')&&visible(candidate));
        const purchase=main?.querySelector('.alma-pdp-purchase-zone');
        const media=main?.querySelector('.alma-pdp-media-zone');
        // Match the actual two-column layout, including an absent/hidden gallery.
        const columns=main?getComputedStyle(main.querySelector('.alma-pdp-hero')).gridTemplateColumns.split(' ').filter(Boolean).length:0;
        if(!main||!purchase||!visible(media)||columns<2||innerWidth<Number(main.dataset.breakpoint||1024)){this.restore(record);record.main=main||null;continue;}
        if(!record.anchor.isConnected){this.restore(record);continue;}
        const whatsapp=[...purchase.children].find(el=>el.dataset.almaBlock==='whatsapp'&&visible(el));
        const previous=placements.get(main)||whatsapp;
        const before=previous?previous.nextSibling:null;
        if(record.host.parentNode!==purchase || (previous ? record.host.previousSibling!==previous : record.host!==purchase.lastChild))this.move(record.host,purchase,before);
        record.host.classList.add('alma-pdp-details-mounted');record.main=main;record.mounted=true;
        placements.set(main,record.host);
        // Hidden sections must not leave the purchase grid's gap behind.
        record.host.classList.remove('alma-pdp-details-empty');
        record.host.classList.toggle('alma-pdp-details-empty',!visible(record.root));
      }
    }finally{this.placing=false;}
  }
  destroy(){
    cancelAnimationFrame(this.frame);this.life.abort();this.observer.disconnect();
    for(const record of this.records.values()){this.restore(record);record.anchor.remove();}
    this.records.clear();
  }
}
if(typeof customElements!=='undefined'){
  for(const [name,constructor] of [['alma-pdp-product',AlmaProduct],['alma-pdp-gallery',AlmaGallery],['alma-pdp-section',AlmaSection]])if(!customElements.get(name))customElements.define(name,constructor);
  const placementKey=Symbol.for('alma.pdp.detailsPlacement');
  if(!window[placementKey]){
    window[placementKey]=new AlmaDetailsPlacement();
    document.addEventListener('shopify:section:reorder',event=>{event.target.querySelectorAll('alma-pdp-gallery').forEach(g=>g.resize());event.target.querySelectorAll('alma-pdp-product').forEach(p=>p.sticky());},{signal:window[placementKey].life.signal});
  }
}